import { useState, useEffect, useRef } from "react";

/**
 * A hook that performs real connectivity checks, not just relying on navigator.onLine
 * Uses external URL to bypass service worker cache
 * @param {Object} options - Configuration options
 * @param {string} options.pingUrl - External URL to ping (default: "https://www.google.com/favicon.ico")
 * @param {number} options.intervalMs - Polling interval in milliseconds (default: 5000)
 * @param {number} options.timeoutMs - Request timeout in milliseconds (default: 2000)
 * @returns {{ isOnline: boolean, isChecking: boolean }} - Online status and checking state
 */
const useRealOnlineStatus = ({
    pingUrl = "https://www.google.com/favicon.ico", // External URL to bypass SW cache
    intervalMs = 5000,
    timeoutMs = 2000,
} = {}) => {
    // Start with navigator.onLine but immediately verify with ping
    const [isOnline, setIsOnline] = useState(() => navigator.onLine);
    const [isChecking, setIsChecking] = useState(true); // Start as checking
    const intervalRef = useRef(null);
    const abortControllerRef = useRef(null);
    const timeoutIdRef = useRef(null);

    useEffect(() => {
        const performPing = async () => {
            // If navigator.onLine is false, immediately set offline (don't wait for ping)
            // This ensures banner shows immediately when browser reports offline
            if (!navigator.onLine) {
                setIsOnline(false);
                setIsChecking(false);
                return;
            }
            
            setIsChecking(true);

            // Abort any ongoing request and clear any pending timeout
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
            if (timeoutIdRef.current) {
                clearTimeout(timeoutIdRef.current);
            }

            // Create new AbortController for this request
            const abortController = new AbortController();
            abortControllerRef.current = abortController;

            // Add cache-busting query param to ensure fresh request (never use cache)
            const urlWithCacheBust = `${pingUrl}${pingUrl.includes('?') ? '&' : '?'}_ping=${Date.now()}&_r=${Math.random()}`;

            // Create a timeout promise that rejects
            const timeoutPromise = new Promise((_, reject) => {
                timeoutIdRef.current = setTimeout(() => {
                    abortController.abort();
                    reject(new Error('Network timeout'));
                }, timeoutMs);
            });

            try {
                // Race between fetch and timeout
                // Use no-cors mode to avoid CORS issues - we just need to know if network is available
                // With no-cors, we can't read response.ok, but if the request completes, network is available
                const fetchPromise = fetch(urlWithCacheBust, {
                    method: "GET",
                    cache: "no-store",
                    signal: abortController.signal,
                    mode: "no-cors", // Bypass CORS - we just need to know if network request succeeds
                });

                const response = await Promise.race([fetchPromise, timeoutPromise]);

                // Clear timeout since we got a response
                if (timeoutIdRef.current) {
                    clearTimeout(timeoutIdRef.current);
                    timeoutIdRef.current = null;
                }

                // With no-cors mode, response.type will be 'opaque' if successful
                // If we get any response (even opaque), it means network is available
                if (response && (response.ok || response.type === 'opaque' || response.type === 'basic')) {
                    setIsOnline(true);
                } else {
                    setIsOnline(false);
                }
            } catch (error) {
                // Clear timeout
                if (timeoutIdRef.current) {
                    clearTimeout(timeoutIdRef.current);
                    timeoutIdRef.current = null;
                }

                // Any error (network error, timeout, abort, etc.) means we're offline
                // Only update if this is still the current request
                if (abortControllerRef.current === abortController) {
                    setIsOnline(false);
                }
            } finally {
                // Only set isChecking to false if this is still the current request
                if (abortControllerRef.current === abortController) {
                    setIsChecking(false);
                }
            }
        };

        // Check navigator.onLine first, then perform initial ping
        if (!navigator.onLine) {
            setIsOnline(false);
            setIsChecking(false);
        } else {
            performPing();
        }

        const handleOnline = () => {
            // When browser reports online, verify with ping
            performPing();
        };

        const handleOffline = () => {
            // When browser reports offline, immediately set to offline
            setIsOnline(false);
            setIsChecking(false);
        };

        // Set up event listeners
        window.addEventListener("online", handleOnline);
        window.addEventListener("offline", handleOffline);

        // Set up polling interval
        intervalRef.current = setInterval(() => {
            performPing();
        }, intervalMs);

        // Cleanup function
        return () => {
            window.removeEventListener("online", handleOnline);
            window.removeEventListener("offline", handleOffline);
            
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }

            if (timeoutIdRef.current) {
                clearTimeout(timeoutIdRef.current);
            }

            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        };
    }, [pingUrl, intervalMs, timeoutMs]);

    return { isOnline, isChecking };
};

export default useRealOnlineStatus;
